import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useDraggable } from '@dnd-kit/core';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Eye, MoreVertical, Pencil, BarChart3, Download, Code, Trash2, FileEdit, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { CopyCodeModal } from '@/components/shared/CopyCodeModal';
import { downloadVideo, downloadVideoDirectLink } from '@/utils/downloadVideo';
import { useVideoDashboardStore } from '@/store/videoDashboard';
import { toast as sonnerToast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

interface VideoCardProps {
  id: string;
  title: string;
  thumbnailUrl: string;
  viewCount?: number;
  isProcessing?: boolean;
  isNew?: boolean;
  className?: string;
  style?: React.CSSProperties;
  folderId?: string | null;
  createdAt?: string;
  onCardClick?: (video: { id: string; title: string; thumbnailUrl: string; viewCount: number; createdAt?: string }) => void;
}

export function VideoCard({
  id,
  title,
  thumbnailUrl,
  viewCount = 0,
  isProcessing = false,
  isNew = false,
  className,
  style,
  folderId,
  createdAt,
  onCardClick,
}: VideoCardProps) {
  useLanguage();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const videos = useVideoDashboardStore(state => state.videos);
  
  const currentVideo = videos.find(v => v.id === id);
  
  const defaultFeatures = {
    autoPlay: false,
    showControls: true,
    loop: false,
    loopVideo: false,
  };
  
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: id,
    data: {
      type: 'video',
      videoId: id,
      folderId: folderId
    }
  });


  const handleSaveRename = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    
    const trimmedTitle = editedTitle.trim();
    if (trimmedTitle && trimmedTitle !== title) {
      if (trimmedTitle.length > 100) {
        toast({
          title: translateInterfaceText("Erro ao renomear"),
          description: translateInterfaceText("O título deve ter no máximo 100 caracteres"),
          variant: "destructive",
        });
        return;
      }
      // Atualizar no banco de dados (implementar depois)
      console.log('Renomear vídeo:', id, 'para:', trimmedTitle);
      toast({
        title: translateInterfaceText("Vídeo renomeado"),
        description: translateInterfaceText("Nome alterado para \"{value0}\"", { value0: (trimmedTitle) }),
      });
    }
    setIsEditing(false);
  };

  const handleCancelRename = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setEditedTitle(title);
    setIsEditing(false);
  };

  const handleBlur = () => {
    // Delay para permitir que o clique nos botões seja processado primeiro
    blurTimeoutRef.current = setTimeout(() => {
      handleSaveRename();
    }, 200);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveRename();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelRename();
    }
  };

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const handleAction = (action: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    switch (action) {
      case 'personalize':
        navigate(`/personalizations/${id}`);
        break;
      case 'rename':
        setIsEditing(true);
        setEditedTitle(title);
        setTimeout(() => inputRef.current?.focus(), 50);
        break;
      case 'metrics':
        navigate(`/metricas/${id}`);
        break;
      case 'download':
        if (currentVideo?.videoUrl) {
          downloadVideo(currentVideo.videoUrl, title);
        } else if (thumbnailUrl) {
          // Fallback: usar thumbnail URL como alternativa
          downloadVideoDirectLink(thumbnailUrl, title);
        } else {
          sonnerToast.error('URL do vídeo não disponível');
        }
        break;
      case 'copy-code':
        setShowEmbedModal(true);
        break;
      case 'delete':
        setShowDeleteConfirm(true);
        break;
    }
  };

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
    >
      <Card
        ref={setNodeRef}
        style={{
          transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
          cursor: isDragging ? 'grabbing' : 'pointer',
          opacity: isDragging ? 0.5 : 1,
          ...style
        }}
        className={cn(
          "relative overflow-hidden transition-all duration-200 border border-[rgb(var(--border-light))] bg-[rgb(var(--surface-primary))] shadow-sm hover:shadow-md",
          isDragging && "shadow-2xl ring-2 ring-primary",
          className
        )}
        {...attributes}
        {...listeners}
      >
        {/* Thumbnail Container */}
        <div className="relative aspect-video overflow-hidden bg-gray-100 dark:bg-muted">
          <img
            src={thumbnailUrl}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
          />
          
          {/* Processing Badge */}
          {isProcessing && (
            <Badge 
              className="absolute top-1.5 left-1.5 sm:top-2 sm:left-2 bg-primary text-white px-2 py-0.5 text-[10px] sm:text-xs font-medium h-5 sm:h-6"
            >
              {translateInterfaceText("Processando")}</Badge>
          )}
          
          {/* New Badge */}
          {isNew && !isProcessing && (
            <Badge 
              className="absolute top-1.5 left-1.5 sm:top-2 sm:left-2 bg-primary text-white px-2 py-0.5 text-[10px] sm:text-xs font-medium h-5 sm:h-6"
            >
              {translateInterfaceText("Novo")}</Badge>
          )}
          
        </div>

        {/* Card Footer */}
        <CardContent className="p-3">
          {/* Linha única: Título + Visualizações + Menu */}
          <div className="flex items-center justify-between gap-2">
            {/* Coluna esquerda: Título (ou modo de edição) */}
            {isEditing ? (
              <div className="flex items-center gap-1 flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
                <Input
                  ref={inputRef}
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  className="h-7 text-xs font-medium"
                  maxLength={100}
                />
                <div className="flex gap-0.5 shrink-0">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-green-600 hover:text-green-700 hover:bg-green-100 dark:hover:bg-green-900/30"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveRename();
                    }}
                  >
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-red-600 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelRename();
                    }}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex-1 min-w-0">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <h3 className="font-semibold text-[15px] leading-[22px] line-clamp-1 text-foreground" title={title}>
                        {title}
                      </h3>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{title}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
            
            {/* Coluna direita: Visualizações + Menu */}
            <div className="flex items-center gap-2 shrink-0">
              {/* Visualizações */}
              <div className="flex items-center gap-1 text-[12px] text-muted-foreground">
                <Eye className="h-3.5 w-3.5" />
                <span className="whitespace-nowrap">
                  {viewCount > 999 ? `${(viewCount/1000).toFixed(1)}k` : viewCount}
                </span>
              </div>
              
              {/* Menu Kebab */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6 opacity-100 hover:bg-transparent"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={(e) => handleAction('personalize', e)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Personalizar")}</DropdownMenuItem>
                  
                  <DropdownMenuItem onClick={(e) => handleAction('rename', e)}>
                    <FileEdit className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Renomear")}</DropdownMenuItem>
                  
                  <DropdownMenuItem onClick={(e) => handleAction('metrics', e)}>
                    <BarChart3 className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Ver Métricas")}</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={(e) => handleAction('download', e)}>
                    <Download className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Download")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => handleAction('copy-code', e)}>
                    <Code className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Copiar código")}</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={(e) => handleAction('delete', e)}
                    className="text-red-600 data-[highlighted]:bg-red-600 data-[highlighted]:text-white dark:text-red-400"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Deletar")}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <DeleteConfirmModal
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        videoId={id}
        videoTitle={title}
      />
      
      <CopyCodeModal
        isOpen={showEmbedModal}
        onClose={() => setShowEmbedModal(false)}
        entityType="video"
        entityId={id}
        entityName={title}
        userId={user?.id_user}
        videoOrigin="ORIGINAL"
      />
    </motion.div>
  );
}
